import { Button } from "../Button/index.js";
import { Dropdown } from "../Dropdown/index.js";
import { Portal } from "../Portal/index.js";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IconArrowTop, IconClose, IconMenuHorizontalDots } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { FocusTrap } from "focus-trap-react";
//#region src/Sidebar/index.tsx
const widths = {
	sm: "30rem",
	md: "60rem"
};
const durations = {
	sm: 300,
	md: 400
};
const sidebarShadow = "0px 23px 43px 0px rgba(32, 26, 26, 0.11), 0px 5px 9px 0px rgba(32, 26, 26, 0.06), 0px 2px 3px 0px rgba(32, 26, 26, 0.04)";
const navButtonClassName = "size-8 rounded-[0.625rem] border-none";
const navIconClassName = "text-neutrals-60 size-4";
const SidebarPortal = memo(({ isOpen, sidebarData, delayedUnmountSidebar, isMounted }) => {
	const { header, body, options = {}, footer } = sidebarData;
	const { size = "sm", disableKeyboardEvents = false, withFocusTrap = true } = options;
	const ref = useRef(null);
	const layoutRef = useRef(null);
	const prevButtonRef = useRef(null);
	const nextButtonRef = useRef(null);
	const closeButtonRef = useRef(null);
	const handleClickLayout = (e) => {
		const isSidebarContent = ref.current?.contains(e.target);
		if (isOpen && !isSidebarContent) {
			const root = document.getElementById("root");
			const isClickOnLayout = e.target === layoutRef.current;
			const isClickOnAnotherPortal = root ? !root.contains(e.target) : false;
			if (isClickOnLayout || isClickOnAnotherPortal) delayedUnmountSidebar();
		}
	};
	const dropdown = !options.headerHideMenu && header?.sidebarMenu ? /* @__PURE__ */ jsx("div", {
		className: "ml-auto",
		children: /* @__PURE__ */ jsx(Dropdown, {
			trigger: /* @__PURE__ */ jsx(Button, {
				theme: "secondary",
				className: "ml-auto",
				leftIcon: /* @__PURE__ */ jsx(IconMenuHorizontalDots, { className: "size-5" }),
				isFullWidth: false
			}),
			scheme: header.sidebarMenu.scheme,
			state: header.sidebarMenu.state,
			onChange: header.sidebarMenu.onChange
		})
	}) : null;
	const headerActionButton = header?.sidebarButtonIcon && header.sidebarButtonOnClick ? /* @__PURE__ */ jsx(Button, {
		className: "ml-auto",
		leftIcon: header.sidebarButtonIcon,
		onClick: header.sidebarButtonOnClick,
		theme: "text"
	}) : null;
	useEffect(() => {
		const actions = {
			ArrowDown: () => {
				header?.onClickHeaderNextButton?.();
				nextButtonRef.current?.focus();
			},
			ArrowUp: () => {
				header?.onClickHeaderPrevButton?.();
				prevButtonRef.current?.focus();
			},
			Escape: () => {
				if (document.activeElement === closeButtonRef.current) {
					delayedUnmountSidebar();
					return;
				}
				closeButtonRef.current?.focus();
			}
		};
		const cb = (e) => {
			actions[e.key]?.();
		};
		if (!disableKeyboardEvents && header?.onClickHeaderNextButton && header?.onClickHeaderPrevButton) document.addEventListener("keydown", cb);
		return () => document.removeEventListener("keydown", cb);
	}, [
		header,
		disableKeyboardEvents,
		delayedUnmountSidebar
	]);
	const duration = durations[size] ?? durations.sm;
	const panelWidth = options.width || widths[size] || options.width || widths.sm;
	const closeButton = /* @__PURE__ */ jsx(Button, {
		theme: "elevated",
		size: "sm",
		onClick: delayedUnmountSidebar,
		className: cn("absolute left-0 size-8 -translate-x-1/2 rounded-[0.625rem] border-none", options.headerHide && "top-2 z-10"),
		leftIcon: /* @__PURE__ */ jsx(IconClose, { className: navIconClassName }),
		ref: closeButtonRef
	});
	const innerContent = /* @__PURE__ */ jsx("div", {
		ref: layoutRef,
		className: "fixed inset-0",
		onClick: handleClickLayout,
		role: "presentation",
		children: isMounted && /* @__PURE__ */ jsxs("div", {
			ref,
			className: cn("bg-neutrals-99 absolute right-0 top-0 z-50 flex h-screen translate-x-full flex-col transition-transform ease-linear", isOpen && "translate-x-0"),
			style: {
				width: panelWidth,
				transitionDuration: `${duration}ms`,
				transitionProperty: "transform",
				boxShadow: sidebarShadow
			},
			children: [
				options.headerHide ? closeButton : /* @__PURE__ */ jsxs("header", {
					className: "bg-neutrals-99 flex min-h-[3rem] items-center gap-2 overflow-x-visible px-6 py-2",
					children: [
						closeButton,
						!options.headerHideButtons && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Button, {
							onClick: header?.onClickHeaderPrevButton,
							theme: "text",
							size: "sm",
							className: navButtonClassName,
							leftIcon: /* @__PURE__ */ jsx(IconArrowTop, { className: navIconClassName }),
							ref: prevButtonRef
						}), /* @__PURE__ */ jsx(Button, {
							onClick: header?.onClickHeaderNextButton,
							theme: "text",
							size: "sm",
							className: cn(navButtonClassName, "cursor-pointer"),
							leftIcon: /* @__PURE__ */ jsx(IconArrowTop, { className: cn(navIconClassName, "rotate-180") }),
							ref: nextButtonRef
						})] }),
						!options.headerHideTitle && header?.title && /* @__PURE__ */ jsx("h6", {
							className: "overflow-hidden truncate text-lg font-medium leading-normal",
							children: header.title
						}),
						dropdown,
						!options.headerHideButton && headerActionButton
					]
				}),
				/* @__PURE__ */ jsx("div", {
					className: "[&::-webkit-scrollbar-thumb]:bg-neutrals-80 [&::-webkit-scrollbar-track]:bg-neutrals-white h-full overflow-y-auto break-words p-6 text-base text-zinc-600 [&::-webkit-scrollbar-thumb]:rounded-[10px] [&::-webkit-scrollbar]:w-2",
					children: body.content
				}),
				footer?.content && /* @__PURE__ */ jsx("footer", {
					className: "w-full p-6 pt-4",
					children: footer.content
				})
			]
		})
	});
	return /* @__PURE__ */ jsx(Portal, { children: withFocusTrap ? /* @__PURE__ */ jsx(FocusTrap, {
		focusTrapOptions: { allowOutsideClick: true },
		children: innerContent
	}) : innerContent });
});
SidebarPortal.displayName = "SidebarPortal";
const Sidebar = memo(({ children }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isMounted, setIsMounted] = useState(false);
	const [sidebarData, setSidebarData] = useState({ body: {} });
	const size = sidebarData?.options?.size || "sm";
	const delayedUnmountSidebar = useCallback(() => {
		setIsOpen(false);
		const duration = durations[size] ?? durations.sm;
		setTimeout(() => {
			setIsMounted(false);
		}, duration);
	}, [size]);
	const providerValue = useMemo(() => ({
		open: (data) => {
			setSidebarData(data);
			setIsMounted(true);
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setIsOpen(true);
				});
			});
		},
		close: delayedUnmountSidebar
	}), [delayedUnmountSidebar]);
	const onCloseCallback = sidebarData.onCloseCallback;
	useEffect(() => {
		document.body.style.overflowY = isOpen ? "hidden" : "auto";
		return () => {
			if (isOpen) onCloseCallback?.();
		};
	}, [isOpen, onCloseCallback]);
	return /* @__PURE__ */ jsxs(SidebarContext.Provider, {
		value: providerValue,
		children: [children, isMounted && /* @__PURE__ */ jsx(SidebarPortal, {
			isOpen,
			isMounted,
			sidebarData,
			delayedUnmountSidebar
		})]
	});
});
Sidebar.displayName = "Sidebar";
const SidebarContext = createContext(null);
const useSidebar = () => {
	const context = useContext(SidebarContext);
	if (!context) throw new Error("The application must be wrapped in a Sidebar component");
	return context;
};
const tableNavMethods = {
	getNextIndex(data, currentIndex) {
		const depthLevel = data.rows[currentIndex]?.depth;
		for (let i = currentIndex + 1; i < data.rows.length; i++) if (data.rows[i]?.depth === depthLevel) return i;
		return currentIndex;
	},
	getPrevIndex(data, currentIndex) {
		const depthLevel = data.rows[currentIndex]?.depth;
		for (let i = currentIndex - 1; i > -1; i--) if (data.rows[i]?.depth === depthLevel) return i;
		return currentIndex;
	}
};
/**
* Хук для связки таблицы и сайдбара. Расширяет возможности стандартного useSidebar.
*/
const useSidebarWithTable = (callback, options = {}) => {
	const context = useContext(SidebarContext);
	if (!context) throw new Error("The application must be wrapped in a Sidebar component");
	const [currentActiveRow, setCurrentActiveRow] = useState(null);
	const onRowClick = useCallback((data) => {
		const { row, rows, cell } = data;
		const currIndex = rows.indexOf(row);
		const callbackReturn = callback({
			row,
			rows,
			cell
		});
		if (!callbackReturn) return;
		const { header = {}, body, ...rest } = callbackReturn;
		let prevRow;
		let nextRow;
		if (!options.inDepth) {
			const prevIndex = tableNavMethods.getPrevIndex(data, currIndex);
			const nextIndex = tableNavMethods.getNextIndex(data, currIndex);
			prevRow = rows[prevIndex] ?? row;
			nextRow = rows[nextIndex] ?? row;
		} else {
			const prevIndex = currIndex - 1 >= 0 ? currIndex - 1 : 0;
			const nextIndex = currIndex + 1 <= rows.length - 1 ? currIndex + 1 : currIndex;
			prevRow = rows[prevIndex] ?? row;
			nextRow = rows[nextIndex] ?? row;
		}
		context.open({
			header: {
				...header,
				onClickHeaderPrevButton: () => onRowClick({
					row: prevRow,
					rows,
					cell
				}),
				onClickHeaderNextButton: () => onRowClick({
					row: nextRow,
					rows,
					cell
				})
			},
			body,
			...rest,
			onCloseCallback: () => {
				rest.onCloseCallback?.();
				setCurrentActiveRow(null);
			}
		});
		setCurrentActiveRow(row);
	}, [
		callback,
		context,
		options.inDepth
	]);
	return {
		...context,
		currentActiveRow,
		onRowClick
	};
};
//#endregion
export { Sidebar, useSidebar, useSidebarWithTable };

//# sourceMappingURL=index.js.map